import { useRef, useState } from 'react'
import { Popover, PopoverAnchor, PopoverContent } from '@/components/ui/popover'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Label } from '@/components/ui/label'
import { Plus, X } from 'lucide-react'

type TagComboboxProps = {
  label: string
  placeholder?: string
  suggestions: string[]
  selected: string[]
  onChange: (items: string[]) => void
  allowCustom?: boolean
  maxItems?: number
  maxLength?: number
  error?: string | string[]
}

export function TagCombobox({
  label,
  placeholder = 'Search...',
  suggestions,
  selected,
  onChange,
  allowCustom = false,
  maxItems,
  maxLength,
  error,
}: TagComboboxProps) {
  const [query, setQuery] = useState('')
  const [open, setOpen] = useState(false)
  const inputRef = useRef<HTMLInputElement>(null)
  const anchorRef = useRef<HTMLDivElement>(null)

  const trimmed = query.trim()
  const normalizedQuery = trimmed.toLowerCase()
  const atLimit = maxItems !== undefined && selected.length >= maxItems
  const errorMessage = Array.isArray(error) ? error[0] : error

  function suggestionRank(item: string) {
    const normalizedItem = item.toLowerCase()
    if (normalizedItem.startsWith(normalizedQuery)) return 0
    if (normalizedItem.split(/[\s&/-]+/).some(word => word.startsWith(normalizedQuery))) return 1
    return 2
  }

  const filtered = suggestions
    .filter(s => !selected.some(sel => sel.toLowerCase() === s.toLowerCase()))
    .filter(s => s.toLowerCase().includes(normalizedQuery))
    .sort((a, b) => {
      const rankDiff = suggestionRank(a) - suggestionRank(b)
      return rankDiff === 0 ? a.localeCompare(b) : rankDiff
    })

  const alreadyExists = suggestions.some(s => s.toLowerCase() === trimmed.toLowerCase())
    || selected.some(s => s.toLowerCase() === trimmed.toLowerCase())
  const showAddCustom = allowCustom && !atLimit && trimmed.length > 0
    && filtered.length === 0 && !alreadyExists

  function selectItem(item: string) {
    if (atLimit) return
    onChange([...selected, item])
    setQuery('')
    setOpen(false)
    inputRef.current?.blur()
  }

  function removeItem(item: string) {
    onChange(selected.filter(i => i !== item))
  }

  function handleKeyDown(e: React.KeyboardEvent) {
    if (e.key === 'Enter') {
      e.preventDefault()
      if (filtered.length > 0) {
        selectItem(filtered[0])
      } else if (showAddCustom) {
        selectItem(trimmed)
      }
    }
  }

  return (
    <div className="space-y-3">
      <Label>{label}</Label>

      <Popover open={open && !atLimit} onOpenChange={(value) => setOpen(value && !atLimit)}>
        <PopoverAnchor asChild>
          <div ref={anchorRef}>
            <Input
              ref={inputRef}
              placeholder={atLimit ? `Maximum ${maxItems} selections reached` : placeholder}
              value={query}
              maxLength={maxLength}
              aria-invalid={Boolean(errorMessage)}
              onChange={(e) => {
                setQuery(e.target.value)
                if (!open) setOpen(true)
              }}
              onFocus={() => { if (!atLimit) setOpen(true) }}
              onKeyDown={handleKeyDown}
              disabled={atLimit}
            />
          </div>
        </PopoverAnchor>

        <PopoverContent
          onOpenAutoFocus={(e) => e.preventDefault()}
          onInteractOutside={(e) => {
            if (anchorRef.current?.contains(e.target as Node)) {
              e.preventDefault()
            }
          }}
          align="start"
          sideOffset={4}
          className="w-(--radix-popover-trigger-width) max-h-60 gap-0 overflow-y-auto rounded-lg bg-popover p-1 text-popover-foreground shadow-md ring-1 ring-foreground/10"
        >
          {filtered.map(item => (
            <div
              key={item}
              className="cursor-default rounded-md px-1.5 py-1 text-sm select-none hover:bg-accent hover:text-accent-foreground"
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => selectItem(item)}
            >
              {item}
            </div>
          ))}

          {showAddCustom && (
            <>
              {filtered.length > 0 && <div className="-mx-1 my-1 h-px bg-border" />}
              <div
                className="flex items-center gap-1.5 cursor-default rounded-md px-1.5 py-1 text-sm select-none hover:bg-accent hover:text-accent-foreground text-primary"
                onMouseDown={(e) => e.preventDefault()}
                onClick={() => selectItem(trimmed)}
              >
                <Plus className="size-3.5" />
                Add &ldquo;{trimmed}&rdquo;
              </div>
            </>
          )}

          {filtered.length === 0 && !showAddCustom && (
            <div className="px-1.5 py-2 text-sm text-muted-foreground">No matches found</div>
          )}
        </PopoverContent>
      </Popover>

      {errorMessage && (
        <p className="text-base text-destructive">{errorMessage}</p>
      )}

      {selected.length > 0 && (
        <div className="flex flex-wrap gap-2">
          {selected.map(item => (
            <Badge key={item} variant="default" className="gap-1 pr-1">
              {item}
              <button
                type="button"
                className="ml-0.5 rounded-full p-0.5 hover:bg-primary-foreground/20"
                onClick={() => removeItem(item)}
              >
                <X className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  )
}
